/**
 * ProductListView — Shopify-style product list.
 *
 * Clean card rows: thumbnail · name · group · variant count · price.
 * Works on both mobile and desktop. Variants expand under parent items.
 */

import { useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ChevronDown, Search, Package, Plus, Filter, X, Check } from "lucide-react";
import { getERPClient } from "@/lib/erp/client";
import { cn } from "@/lib/utils";
import type { ItemWithVariants } from "@/lib/erp/types/item";
import { BundleBadge } from "../item-detail/components/BundleBadge";
import {
  useCollections,
  useCollectionProducts,
  useStorefrontTenantKey,
} from "@/features/storefront/collections/hooks";

// ---------------------------------------------------------------------------
// Hook: bulk item → collection title map
// ---------------------------------------------------------------------------

const API_BASE = "zaviago_backend.storefront.api_collections";

/** Fetches products for ALL collections and builds a reverse map: item_code → collection titles */
function useItemCollectionMap(tenantKey: string | null, collections: { name: string; title: string }[]) {
  return useQuery({
    queryKey: ["item-collection-map", tenantKey, collections.map((c) => c.name).join(",")],
    queryFn: async () => {
      const client = getERPClient();
      const map = new Map<string, string[]>();
      // Fetch products for each collection in parallel
      const results = await Promise.all(
        collections.map(async (col) => {
          const res = await client.call<{ items: { item_code: string }[] }>(
            `${API_BASE}.get_collection_products`,
            { tenant: tenantKey, collection_name: col.name },
          );
          return { title: col.title, items: res.items ?? [] };
        }),
      );
      for (const { title, items } of results) {
        for (const item of items) {
          const existing = map.get(item.item_code) ?? [];
          existing.push(title);
          map.set(item.item_code, existing);
        }
      }
      return map;
    },
    enabled: !!tenantKey && collections.length > 0,
    staleTime: 2 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProductListViewProps {
  items: ItemWithVariants[];
  bundleItemCodes: Set<string>;
  onItemClick: (name: string, label?: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isLoading?: boolean;
  totalCount?: number;
  /** Header actions slot (e.g. archive toggle dropdown) */
  headerActions?: React.ReactNode;
  /** Callback for creating a new product */
  onNewClick?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatPrice(rate: number | undefined): string | null {
  if (rate == null || rate === 0) return null;
  return `฿${rate.toLocaleString("th-TH", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function getImageUrl(item: { primary_image?: string; image?: string }): string | null {
  return item.primary_image || item.image || null;
}

// ---------------------------------------------------------------------------
// Filter types & button
// ---------------------------------------------------------------------------

type FilterMode = "category" | "collection";

interface ActiveFilter {
  mode: FilterMode;
  value: string; // item_group name or collection name (doc name)
  label: string; // display label
}

function ProductFilterDrawer({
  categories,
  collections,
  activeFilter,
  onSelect,
  onClear,
}: {
  categories: string[];
  collections: { name: string; title: string }[];
  activeFilter: ActiveFilter | null;
  onSelect: (filter: ActiveFilter) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [tab, setTab] = useState<FilterMode>("collection");
  const hasOptions = categories.length > 0 || collections.length > 0;
  if (!hasOptions) return null;

  const dismiss = () => {
    setClosing(true);
  };

  const handleAnimationEnd = () => {
    if (closing) {
      setOpen(false);
      setClosing(false);
    }
  };

  const handleSelect = (filter: ActiveFilter) => {
    onSelect(filter);
    dismiss();
  };

  const handleClear = () => {
    onClear();
    dismiss();
  };

  const items = tab === "collection"
    ? collections.map((c) => ({ key: c.name, label: c.title, mode: "collection" as const, value: c.name }))
    : categories.map((g) => ({ key: g, label: g, mode: "category" as const, value: g }));

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => { setOpen(true); setClosing(false); }}
          className={cn(
            "shrink-0 h-9 flex items-center gap-1.5 rounded-lg border text-sm transition-colors",
            activeFilter
              ? "border-foreground bg-foreground text-background px-2.5"
              : "border-border/60 bg-gray-50 dark:bg-neutral-900 text-muted-foreground px-2.5 hover:text-foreground",
          )}
        >
          <Filter className="h-3.5 w-3.5" />
          <span className="max-w-[140px] truncate">
            {activeFilter?.label ?? "Filter"}
          </span>
        </button>
        {activeFilter && (
          <button
            type="button"
            onClick={onClear}
            className="shrink-0 h-6 w-6 flex items-center justify-center rounded-full bg-gray-200 dark:bg-neutral-700 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Bottom sheet — portal to body so it escapes all stacking contexts */}
      {open && createPortal(
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className={cn("absolute inset-0 bg-black/40 filter-drawer-backdrop", closing && "closing")}
            onClick={dismiss}
          />
          {/* Panel — 85vh height, rounded-t-[20px], iPhone sheet animation */}
          <div
            className={cn(
              "absolute bottom-0 left-0 right-0 flex flex-col",
              "h-[85vh] rounded-t-[20px] bg-white dark:bg-gray-900",
              "shadow-[0_-10px_40px_-5px_rgba(0,0,0,0.15)]",
              "filter-drawer-panel",
              closing && "closing",
            )}
            style={{ backfaceVisibility: "hidden" }}
            onAnimationEnd={handleAnimationEnd}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>

            {/* Header with tabs */}
            <div className="px-4 pb-3 shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold">Filter by</h3>
                {activeFilter && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-sm text-blue-600 dark:text-blue-400"
                  >
                    Clear
                  </button>
                )}
              </div>
              {/* Segmented control */}
              <div className="flex bg-gray-100 dark:bg-neutral-800 rounded-lg p-0.5">
                <button
                  type="button"
                  onClick={() => setTab("collection")}
                  className={cn(
                    "flex-1 py-2 text-sm font-medium rounded-md transition-all",
                    tab === "collection"
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  Collections
                </button>
                <button
                  type="button"
                  onClick={() => setTab("category")}
                  className={cn(
                    "flex-1 py-2 text-sm font-medium rounded-md transition-all",
                    tab === "category"
                      ? "bg-background shadow-sm text-foreground"
                      : "text-muted-foreground",
                  )}
                >
                  Categories
                </button>
              </div>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto px-2 pb-8">
              <button
                type="button"
                onClick={handleClear}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors",
                  !activeFilter && "bg-gray-50 dark:bg-neutral-800/60",
                )}
              >
                <span className="flex-1 text-sm">All products</span>
                {!activeFilter && <Check className="h-4 w-4 text-blue-600" />}
              </button>
              {items.map((item) => {
                const isActive = activeFilter?.mode === item.mode && activeFilter.value === item.value;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => handleSelect({ mode: item.mode, value: item.value, label: item.label })}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors",
                      isActive && "bg-gray-50 dark:bg-neutral-800/60",
                    )}
                  >
                    <span className="flex-1 text-sm">{item.label}</span>
                    {isActive && <Check className="h-4 w-4 text-blue-600" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// ProductRow
// ---------------------------------------------------------------------------

function ProductRow({
  item,
  isVariant,
  isBundle,
  hasVariants,
  variantCount,
  isExpanded,
  onToggleExpand,
  onItemClick,
  collectionTitles,
}: {
  item: ItemWithVariants;
  isVariant: boolean;
  isBundle: boolean;
  hasVariants: boolean;
  variantCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onItemClick: (name: string, label?: string) => void;
  collectionTitles?: string[];
}) {
  const imageUrl = getImageUrl(item);
  const price = formatPrice(item.standard_rate);
  const isDisabled = item.disabled === 1;

  return (
    <button
      type="button"
      onClick={() => onItemClick(item.name, item.item_name)}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-3.5 text-left transition-colors",
        "hover:bg-gray-50 dark:hover:bg-neutral-900 active:bg-gray-100 dark:active:bg-neutral-800",
        "border-b border-border/40 last:border-b-0",
        isVariant && "pl-12 md:pl-14",
        isDisabled && "opacity-50",
      )}
    >
      {/* Thumbnail */}
      <div className={cn(
        "shrink-0 overflow-hidden bg-gray-100 dark:bg-neutral-800 flex items-center justify-center rounded-[9px]",
        isVariant ? "w-9 h-9" : "w-12 h-12",
      )}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.item_name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <Package className={cn(
            "text-gray-300 dark:text-gray-600",
            isVariant ? "h-4 w-4" : "h-5 w-5",
          )} />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className={cn(
            "font-medium line-clamp-2 leading-snug",
            isVariant ? "text-sm" : "text-sm md:text-[15px]",
          )}>
            {item.item_name || item.name}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          {isBundle && <BundleBadge size="sm" className="shrink-0" />}
          {!isVariant && collectionTitles && collectionTitles.length > 0 && (
            <span className="text-xs text-muted-foreground truncate">
              {collectionTitles.join(" · ")}
            </span>
          )}
        </div>
        {/* Variant count badge */}
        {hasVariants && variantCount > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            className="inline-flex items-center gap-0.5 mt-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            <span>{variantCount} variant{variantCount !== 1 ? "s" : ""}</span>
          </button>
        )}
      </div>

      {/* Price + chevron */}
      <div className="shrink-0 flex items-center gap-2">
        {price && (
          <span className="text-sm text-muted-foreground tabular-nums">
            {price}
          </span>
        )}
        <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// ProductListView
// ---------------------------------------------------------------------------

export function ProductListView({
  items,
  bundleItemCodes,
  onItemClick,
  searchQuery,
  onSearchChange,
  isLoading,
  totalCount,
  headerActions,
  onNewClick,
}: ProductListViewProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(() => new Set());
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);

  const toggleExpand = useCallback((name: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // Collections data
  const { tenantKey } = useStorefrontTenantKey();
  const collectionsQuery = useCollections(tenantKey);
  const collections = useMemo(
    () => (collectionsQuery.data ?? []).map((c) => ({ name: c.name, title: c.title })),
    [collectionsQuery.data],
  );

  // Bulk item → collection titles map (for subtitle display)
  const collectionMapQuery = useItemCollectionMap(tenantKey, collections);
  const collectionMap = collectionMapQuery.data;

  // Fetch products in selected collection (only when collection filter is active)
  const collectionProductsQuery = useCollectionProducts(
    tenantKey,
    activeFilter?.mode === "collection" ? activeFilter.value : null,
  );
  const collectionItemCodes = useMemo(() => {
    if (!collectionProductsQuery.data) return null;
    return new Set(collectionProductsQuery.data.map((p) => p.item_code));
  }, [collectionProductsQuery.data]);

  // Extract unique item groups from current items
  const itemGroups = useMemo(() => {
    const groups = new Set<string>();
    for (const item of items) {
      if (item.item_group) groups.add(item.item_group);
    }
    return Array.from(groups).sort();
  }, [items]);

  // Filter items by active filter
  const filteredItems = useMemo(() => {
    if (!activeFilter) return items;
    if (activeFilter.mode === "category") {
      return items.filter((item) => item.item_group === activeFilter.value);
    }
    // Collection filter — match by item_code
    if (!collectionItemCodes) return items; // still loading
    return items.filter((item) => {
      const code = item.item_code ?? item.name;
      return collectionItemCodes.has(code);
    });
  }, [items, activeFilter, collectionItemCodes]);

  const displayCount = activeFilter ? filteredItems.length : totalCount;

  return (
    <div className="min-h-screen bg-background">
      {/* Search bar */}
      <div className="sticky top-12 z-10 bg-background border-b border-border/40 px-3 py-2 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search products..."
            className="w-full h-9 pl-8 pr-3 rounded-lg border border-border/60 bg-gray-50 dark:bg-neutral-900 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
          />
        </div>
        <ProductFilterDrawer
          categories={itemGroups}
          collections={collections}
          activeFilter={activeFilter}
          onSelect={setActiveFilter}
          onClear={() => setActiveFilter(null)}
        />
        {headerActions}
        {onNewClick && (
          <button
            type="button"
            onClick={onNewClick}
            className="shrink-0 h-9 px-3 flex items-center gap-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="text-sm font-medium">New</span>
          </button>
        )}
      </div>

      {/* Count */}
      {displayCount != null && (
        <div className="px-3 py-2 text-xs text-muted-foreground border-b border-border/40">
          {displayCount} product{displayCount !== 1 ? "s" : ""}
          {activeFilter && (
            <span className="ml-1">
              in <span className="font-medium text-foreground">{activeFilter.label}</span>
            </span>
          )}
        </div>
      )}

      {/* List */}
      <div>
        {isLoading && filteredItems.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-5 w-5 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Package className="h-10 w-10 mb-3 text-muted-foreground/30" />
            <p className="text-sm">No products found</p>
          </div>
        ) : (
          filteredItems.map((item) => {
            const itemCode = item.item_code ?? item.name;
            const hasVariants = item.has_variants === 1 && (item.variants?.length ?? 0) > 0;
            const isExpanded = expandedItems.has(item.name);

            return (
              <div key={item.name}>
                <ProductRow
                  item={item}
                  isVariant={false}
                  isBundle={bundleItemCodes.has(itemCode)}
                  hasVariants={hasVariants}
                  variantCount={item.variants?.length ?? 0}
                  isExpanded={isExpanded}
                  onToggleExpand={() => toggleExpand(item.name)}
                  onItemClick={onItemClick}
                  collectionTitles={collectionMap?.get(itemCode)}
                />
                {/* Expanded variants */}
                {hasVariants && isExpanded && item.variants?.map((variant) => (
                  <ProductRow
                    key={variant.name}
                    item={variant as ItemWithVariants}
                    isVariant
                    isBundle={bundleItemCodes.has(variant.item_code ?? variant.name)}
                    hasVariants={false}
                    variantCount={0}
                    isExpanded={false}
                    onToggleExpand={() => {}}
                    onItemClick={onItemClick}
                  />
                ))}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
